import { findLastIndex, isNil, minBy, range, sortBy } from 'lodash';

import type { StpNotification } from '~/components/ClaimPage/CliveApp/CliveNotifications/types';
import type { ClaimStageConfiguration } from '~/components/ClaimStages/types';
import type { ClaimConductorStageHistoryResponseClaimStageHistoryEntry as StageHistoryEntry } from '~/server_shared/openapi/data-contracts';
import { ClaimConductorStageHistoryResponseClaimChangeSource as ChangeSource } from '~/server_shared/openapi/data-contracts';

import { instantOf } from '../../ActionHistory/utils';
import type { ActivityRow, RecommendationRow } from '../../ActivityPanel/types';
import { executedAt, executedStageChangeKey } from '../../hooks/useRefreshClaimOnStageChange';
import { stageMoveDirection } from '../../stageDirection';

import { STAGES_RAIL_TEXTS } from './texts';
import type { ChapterPlace, StageChapter, StageChapterActor } from './types';

const ACTOR_BY_SOURCE: Record<ChangeSource, StageChapterActor> = {
  [ChangeSource.AGENT]: 'agent',
  [ChangeSource.UI]: 'person',
  [ChangeSource.API]: 'person',
  [ChangeSource.BULK]: 'system',
  [ChangeSource.SYSTEM]: 'system',
};

/**
 * One chapter per stage change in `history` (oldest first, as the history read serves it), each named by
 * its stage's configured label — or by its key, for a stage the organization no longer configures, as
 * the server names one — with its direction read against `flow`, the conductor's stage flow.
 */
export const buildStageChapters = (
  history: StageHistoryEntry[],
  flow: string[],
  stages: ClaimStageConfiguration[]
): StageChapter[] => {
  const labelOf = (stageKey: string): string => stages.find(({ key }) => key === stageKey)?.label ?? stageKey;

  return history.map((change, index) => {
    const actor = ACTOR_BY_SOURCE[change.change_source];
    const origin = isNil(change.previous_stage_key)
      ? STAGES_RAIL_TEXTS.CLAIM_OPENED
      : STAGES_RAIL_TEXTS.FROM(labelOf(change.previous_stage_key));

    return {
      // The history only grows, oldest first, so a change keeps its position across reads.
      key: String(index),
      stageKey: change.new_stage_key,
      label: labelOf(change.new_stage_key),
      detail: actor === 'person' && change.changed_by ? STAGES_RAIL_TEXTS.BY(origin, change.changed_by) : origin,
      actor,
      direction: stageMoveDirection(flow, change.previous_stage_key, change.new_stage_key),
      // The server writes UTC without saying so; read as the log's entries are, so the two compare.
      changedAt: instantOf(change.changed_at),
    };
  });
};

/**
 * The index of the chapter the reader is in: the last one the claim had entered by `topEntryAt`, the time of
 * the entry at the top of the log's viewport. At the newest end of the log every chapter has been reached,
 * so it is the last. -1 while the top entry is older than every chapter.
 */
export const activeChapterIndex = (
  chapters: StageChapter[],
  topEntryAt: number | undefined,
  isAtNewestEnd: boolean
): number => {
  if (isAtNewestEnd) return chapters.length - 1;
  if (isNil(topEntryAt)) return -1;

  return findLastIndex(chapters, ({ changedAt }) => changedAt <= topEntryAt);
};

/**
 * Clive's entry about the stage change behind a move it made, in `notifications` and drawn in a box of its own by
 * `isDrawn`: the latest entry about the execution nearest the move, so a second move into the same stage does not
 * borrow the first one's, and only where it is stamped at or after the move. One stamped before is about something
 * earlier: a handler executing Clive's card publishes no entry, so the latest about that card is the proposal.
 * `undefined` for a move Clive did not make, or where there is no such entry.
 */
export const cliveEntryId = (
  chapter: StageChapter,
  rows: ActivityRow[],
  notifications: StpNotification[],
  isDrawn: (eventId: string) => boolean
): string | undefined => {
  if (chapter.actor !== 'agent') return undefined;

  const executions = rows.filter(
    (row): row is RecommendationRow =>
      row.kind === 'recommendation' && executedStageChangeKey(row.action) === chapter.stageKey
  );
  const logEventId = minBy(executions, ({ action }) => Math.abs(executedAt(action) - chapter.changedAt))?.logEventId;
  const entry = notifications.find(({ event_id: eventId }) => eventId === logEventId);

  return entry && isDrawn(entry.event_id) && instantOf(entry.timestamp) >= chapter.changedAt
    ? entry.event_id
    : undefined;
};

/**
 * The log entry a chapter takes the reader to, of those `isDrawn` says the log draws a box of its own for: Clive's
 * entry about a move it made (`cliveEntryId`), though another entry comes first; otherwise the first entry at or after
 * the move in `notifications`, oldest first. Either way it is at or after the move, so once the reader is there the
 * rail does not mark an earlier chapter than the one they picked. `undefined` where there is none.
 */
export const chapterEntryId = (
  chapter: StageChapter,
  rows: ActivityRow[],
  notifications: StpNotification[],
  isDrawn: (eventId: string) => boolean
): string | undefined =>
  cliveEntryId(chapter, rows, notifications, isDrawn) ??
  notifications.find(
    ({ event_id: eventId, timestamp }) => instantOf(timestamp) >= chapter.changedAt && isDrawn(eventId)
  )?.event_id;

/**
 * Of `count` entries stacked top to bottom, the first whose bottom edge is below `top`: the entry at the top of a view
 * whose top edge is there. `count` when every entry ends above it. A binary search, since each `bottomOf` is a read of
 * the layout, the log can hold hundreds of entries, and the rail reads it on every frame the log scrolls in.
 */
export const firstIndexBelow = (count: number, bottomOf: (index: number) => number, top: number): number => {
  let low = 0;
  let high = count;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    if (bottomOf(middle) > top) {
      high = middle;
    } else {
      low = middle + 1;
    }
  }

  return low;
};

/** The least room between two dots down the stages rail, and between two of its labels. */
const RAIL_SPACING_PX = 16;
/** How far the rail's spine stops short of the rail's top and foot: the spine's `inset-y-16`. */
const SPINE_INSET_PX = 16;
/** A chapter's label: its button's `h-40`. */
const LABEL_PX = 40;

/**
 * `points`, in order down a line from `start` to `end`, pushed apart to `spacing` where two are closer, the later one
 * downward, and back up from `end` where that pushed them past it. On a line too short for `spacing` between every
 * two, they share it evenly.
 */
const spreadApart = (points: number[], start: number, end: number, spacing: number): number[] => {
  const last = points.length - 1;
  const room = last > 0 ? Math.min(spacing, (end - start) / last) : spacing;
  const spread = [...points];
  for (let index = 0; index <= last; index += 1) {
    spread[index] = Math.max(spread[index], index === 0 ? start : spread[index - 1] + room);
  }
  for (let index = last; index >= 0; index -= 1) {
    spread[index] = Math.min(spread[index], index === last ? end : spread[index + 1] - room);
  }

  return spread;
};

/**
 * Where the stages rail draws each chapter, down a rail `railHeight` tall, from `offsets`: how far down the log each
 * chapter's entry is, from 0 to 1, or `undefined` for a chapter older than every entry the log draws. A dot is at its
 * offset down the spine, as the scroller puts the entry; a chapter older than the log waits at the log's oldest end,
 * the top or, once the log is reversed, the foot, until its entries are read in. Two dots closer than 16px are drawn
 * 16px apart. Each label is centred on its dot, within the rail and 16px clear of the next, and each row reaches from
 * its label down to the next one, in the order the dots are drawn, so a pointer between two labels is on a row.
 */
export const placeChapters = (
  offsets: (number | undefined)[],
  railHeight: number,
  isReversed: boolean
): ChapterPlace[] => {
  const spineEnd = railHeight - SPINE_INSET_PX;
  const along = offsets.map(
    (offset) => SPINE_INSET_PX + (offset ?? (isReversed ? 1 : 0)) * (spineEnd - SPINE_INSET_PX)
  );
  // Two chapters at one place go in the log's order, newest first once it is reversed.
  const order = sortBy(range(offsets.length), [(index) => along[index], (index) => (isReversed ? -index : index)]);
  const dots = spreadApart(
    order.map((index) => along[index]),
    SPINE_INSET_PX,
    spineEnd,
    RAIL_SPACING_PX
  );
  const labels = spreadApart(dots, LABEL_PX / 2, railHeight - LABEL_PX / 2, LABEL_PX + RAIL_SPACING_PX);

  return offsets.map((_, index) => {
    const rank = order.indexOf(index);

    return {
      dot: dots[rank],
      rowTop: labels[rank] - LABEL_PX / 2,
      rowHeight: rank < order.length - 1 ? labels[rank + 1] - labels[rank] : LABEL_PX,
    };
  });
};
